import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { BasketStore } from '../../stores/basket.store';
import { BasketRepeatableService } from '../../services/basket-repeatable.service';
import { BasketGroup } from '../../models/basket.model';
import { DateRangeLabelComponent } from '@shared/components/date-range-label/date-range-label.component';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { LoginRequiredDialogComponent } from '../login-required-dialog/login-required-dialog.component';
import { SiteStore } from '@core/site/site.store';
import { isExpired, isOngoing } from '../../timeslot-timing';

@Component({
  selector: 'app-basket-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatButtonModule, MatMenuModule, MatChipsModule, DateRangeLabelComponent],
  templateUrl: './basket-item.component.html',
  styleUrl: './basket-item.component.scss',
})
export class BasketItemComponent {
  readonly item = input.required<BasketGroup>();

  protected readonly basket = inject(BasketStore);
  private readonly repeatable = inject(BasketRepeatableService);
  private readonly auth = inject(AuthenticationService);
  private readonly dialog = inject(MatDialog);
  private readonly site = inject(SiteStore);

  protected readonly showRepeat = computed(
    () => this.site.tenantConfig()?.features.basket?.showRepeatableBookings !== false,
  );

  // The drawer can sit open across a slot's start/end — without a ticking "now",
  // isOngoing() would only be correct at the moment item() last changed.
  private readonly now = signal(new Date());

  protected readonly isOngoing = computed(() => isOngoing(this.item().start, this.item().end, this.now()));
  protected readonly isExpired = computed(() => isExpired(this.item().end, this.now()));

  constructor() {
    const timer = setInterval(() => this.now.set(new Date()), 30_000);
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }

  protected async repeat(): Promise<void> {
    if (!this.auth.isAuthenticated()) {
      this.dialog.open(LoginRequiredDialogComponent, { width: '400px', data: { returnUrl: '/basket' } });
      return;
    }

    // Intentionally passes the combined item (merged start/end) — the dialog repeats
    // the full combined span (e.g. 15:00–17:00), not individual constituent slots.
    // This matches abook-admin-app behavior.
    const added = await this.repeatable.open(this.item(), this.basket.items());
    added.forEach((i) => this.basket.addBasketItem(i));
  }
}
